import { Link, useLocation } from 'react-router-dom';
import type { UserRole } from '@serviceready/shared';

interface NavItem {
  icon: string;
  label: string;
  href: string;
}

interface SidebarProps {
  role: UserRole;
  className?: string;
}

const SIDEBAR_NAV: Record<UserRole, NavItem[]> = {
  submitter: [
    { icon: '\uD83D\uDCCB', label: 'My Items', href: '/dashboard' },
    { icon: '\uD83D\uDCC5', label: 'Calendar', href: '/calendar' },
    { icon: '\u2795', label: 'Submit', href: '/submissions/new' },
    { icon: '\uD83D\uDD14', label: 'Alerts', href: '/notifications' },
    { icon: '\u2699\uFE0F', label: 'Settings', href: '/settings/routing' },
  ],
  pastor: [
    { icon: '\uD83D\uDCCA', label: 'Dashboard', href: '/dashboard' },
    { icon: '\uD83D\uDCC5', label: 'Calendar', href: '/calendar' },
    { icon: '\u2705', label: 'Review', href: '/review' },
    { icon: '\uD83D\uDCCB', label: 'Services', href: '/services' },
    { icon: '\uD83E\uDD1D', label: 'Auxiliaries', href: '/auxiliaries' },
    { icon: '\uD83D\uDEE0\uFE0F', label: 'Manage', href: '/manage' },
    { icon: '\uD83D\uDD14', label: 'Alerts', href: '/notifications' },
    { icon: '\u2699\uFE0F', label: 'Settings', href: '/settings' },
  ],
  minister_of_music: [
    { icon: '\uD83D\uDCCA', label: 'Dashboard', href: '/dashboard' },
    { icon: '\uD83D\uDCC5', label: 'Calendar', href: '/calendar' },
    { icon: '\u2705', label: 'Review', href: '/review' },
    { icon: '\uD83C\uDFB5', label: 'Songs', href: '/songs' },
    { icon: '\uD83D\uDEE0\uFE0F', label: 'Manage', href: '/manage' },
    { icon: '\uD83D\uDD14', label: 'Alerts', href: '/notifications' },
    { icon: '\u2699\uFE0F', label: 'Settings', href: '/settings' },
  ],
  musician: [
    { icon: '\uD83C\uDFB5', label: 'My Songs', href: '/my-assignments' },
    { icon: '\uD83D\uDCC5', label: 'Calendar', href: '/calendar' },
    { icon: '\uD83D\uDCCB', label: 'Requests', href: '/submissions/new' },
    { icon: '\uD83D\uDD14', label: 'Alerts', href: '/notifications' },
    { icon: '\u2699\uFE0F', label: 'Settings', href: '/settings/routing' },
  ],
  media_team: [
    { icon: '\uD83D\uDCCA', label: 'Dashboard', href: '/media' },
    { icon: '\uD83D\uDCC5', label: 'Calendar', href: '/calendar' },
    { icon: '\uD83D\uDCCB', label: 'Services', href: '/services' },
    { icon: '\uD83D\uDD14', label: 'Alerts', href: '/notifications' },
    { icon: '\u2699\uFE0F', label: 'Settings', href: '/settings/routing' },
  ],
  admin: [
    { icon: '\uD83D\uDCCA', label: 'Dashboard', href: '/dashboard' },
    { icon: '\uD83D\uDCC5', label: 'Calendar', href: '/calendar' },
    { icon: '\u2705', label: 'Review', href: '/review' },
    { icon: '\uD83D\uDCCB', label: 'Services', href: '/services' },
    { icon: '\uD83E\uDD1D', label: 'Auxiliaries', href: '/auxiliaries' },
    { icon: '\uD83D\uDEE0\uFE0F', label: 'Manage', href: '/manage' },
    { icon: '\uD83D\uDD14', label: 'Alerts', href: '/notifications' },
    { icon: '\u2699\uFE0F', label: 'Settings', href: '/settings' },
  ],
  finance_team: [
    { icon: '\uD83D\uDCCA', label: 'Dashboard', href: '/dashboard' },
    { icon: '\uD83D\uDCC5', label: 'Calendar', href: '/calendar' },
    { icon: '\uD83D\uDCB0', label: 'Finance', href: '/reports/finance' },
    { icon: '\uD83D\uDD14', label: 'Alerts', href: '/notifications' },
    { icon: '\u2699\uFE0F', label: 'Settings', href: '/settings' },
  ],
};

const SIDEBAR_ACTIVE_COLORS: Record<UserRole, string> = {
  submitter: 'text-[var(--color-primary)]',
  pastor: 'text-[var(--color-primary)]',
  minister_of_music: 'text-[var(--color-music)]',
  musician: 'text-[var(--color-music)]',
  media_team: 'text-[var(--color-primary)]',
  admin: 'text-[var(--color-primary)]',
  finance_team: 'text-[var(--color-primary)]',
};

export function Sidebar({ role, className = '' }: SidebarProps) {
  const location = useLocation();
  const pathname = location.pathname;
  const navItems = SIDEBAR_NAV[role];
  const activeColor = SIDEBAR_ACTIVE_COLORS[role];

  return (
    <aside
      className={`hidden md:flex fixed left-0 top-0 h-screen w-64 flex-col bg-[var(--color-bg)] border-r border-[var(--color-border)] z-30 ${className}`}
    >
      {/* App name */}
      <div className="px-5 py-6 border-b border-[var(--color-border)]">
        <h2 className="font-display text-h2 font-bold text-text-primary">
          ServiceReady
        </h2>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const isActive =
            pathname === item.href ||
            (item.href !== '/' && pathname.startsWith(item.href));
          const activeClasses = isActive
            ? `${activeColor} bg-[var(--color-gray-50)] font-semibold`
            : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-gray-50)] hover:text-[var(--color-text)]';

          return (
            <Link
              key={item.href}
              to={item.href}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg)] focus-visible:outline-none ${activeClasses}`}
            >
              <span className="text-lg">{item.icon}</span>
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Bottom section */}
      <div className="px-3 py-4 border-t border-[var(--color-border)]">
        <Link
          to="/profile"
          className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-gray-50)] hover:text-[var(--color-text)] transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg)] focus-visible:outline-none"
        >
          <span className="text-lg">{'\uD83D\uDC64'}</span>
          <span>Profile</span>
        </Link>
      </div>
    </aside>
  );
}
